import { normalizeEvaluatedPriceForDisplay } from "../../../../tools/ItineraryPricingEvaluationDisplay.tools";
import { type ItineraryDTO } from "../../../../types/dtos/api/itinerary/itinerary.dto";
import { type ItineraryPricingPreferencesDTO } from "../../../../types/dtos/api/pricing/itinerary-pricing-preferences.dto";
import { type ProviderOffer } from "../../../../types/dtos/api/sales/provider-offer.dto";
import { SegmentationProviderId } from "../../../../types/itinerary/segmentation-provider.enum";
import { type ItineraryDetailsOfferLinkDisplay } from "../itineraryDetailsOfferLink.types";
import {
    type OfferPricingEvaluationContext,
    type ProviderPricingEvaluation,
    type SegmentOfferDisplayData,
} from "./OfferPricing.types";
import { getOfferPricingProviderPlugin } from "./OfferPricingProvider.registry";
import { selectPreferredEvaluation } from "./OfferPricingProviderSelection.policy";
import { providerLabelFor } from "./helpers/OfferPricingProviderLabel.helper";
import {
    SEGMENT_OFFER_DIRECT_LINK_LABEL,
    SEGMENT_OFFER_PROVIDER_SITE_LINK_LABEL,
} from "./helpers/OfferPricingSegmentDisplay.helper";
import { resolvePricingSegmentProviderUrl } from "./helpers/OfferPricingSegmentProviderUrl.helper";
import { evaluateSwissCffProviderOffers } from "./providers/swiss_cff.OfferPricingProvider";

/**
 * Builds segment link fields: direct offer URL first, then provider-site fallback for opening.
 *
 * @param itinerary - Loaded itinerary.
 * @param segmentId - Pricing segment identifier.
 * @param offerBookingUrl - Booking URL from provider offer evaluation.
 * @returns Offer, provider-site, and resolved booking URLs.
 */
const buildSegmentOfferLinkUrls = (
    itinerary: ItineraryDTO,
    segmentId: string,
    offerBookingUrl: string | null,
): Pick<SegmentOfferDisplayData, "offerBookingUrl" | "providerSiteUrl" | "bookingUrl"> => {
    const providerSiteUrl = offerBookingUrl == null
        ? resolvePricingSegmentProviderUrl( itinerary, segmentId )
        : null;

    return {
        offerBookingUrl,
        providerSiteUrl,
        "bookingUrl": offerBookingUrl ?? providerSiteUrl,
    };
};

const evaluateSegmentProviderOffers = ( context: OfferPricingEvaluationContext ): ProviderPricingEvaluation[] => {
    const providerOffers = context.segmentOffer.providerOffers;
    if ( providerOffers === null ) {
        return [];
    }

    const evaluations: ProviderPricingEvaluation[] = [];
    const nonSwissOffers: ProviderOffer[] = [];
    const swissOffers: ProviderOffer[] = [];

    // Swiss bundles are evaluated together because selection rules apply across all Swiss offers.
    for ( const providerOffer of providerOffers ) {
        if ( providerOffer.provider === "swiss_cff" ) {
            swissOffers.push( providerOffer );
        } else {
            nonSwissOffers.push( providerOffer );
        }
    }

    if ( swissOffers.length > 0 ) {
        evaluations.push( evaluateSwissCffProviderOffers( context, swissOffers ) );
    }

    for ( const providerOffer of nonSwissOffers ) {
        const plugin = getOfferPricingProviderPlugin( providerOffer.provider );
        evaluations.push( plugin.evaluate( context, providerOffer ) );
    }

    return evaluations;
};

/**
 * Computes the display payload for one segment offer button.
 *
 * @remarks When {@link ItineraryPricingPreferencesDTO.swissReductionCard} is `"ga"` (Swiss general
 * subscription), Swiss CFF pricing segments omit a numeric price without requiring a fresh offer
 * response for that preference. Any cached Swiss booking URL from the last evaluation is reused when
 * present.
 */
export const computeSegmentOfferDisplayData = (
    itinerary: ItineraryDTO,
    segmentId: string,
    pricingPreferences: ItineraryPricingPreferencesDTO,
): SegmentOfferDisplayData | null => {
    const segmentOffer = itinerary.offer?.segments[segmentId];
    if ( segmentOffer === undefined ) {
        return null;
    }

    const itinerarySegment = itinerary.pricingSegments?.find( ( segment ) => segment.id === segmentId );
    const isSwissCffPricingSegment = itinerarySegment?.provider?.id === SegmentationProviderId.swiss_cff;

    const context: OfferPricingEvaluationContext = {
        itinerary,
        segmentId,
        segmentOffer,
        pricingPreferences,
    };

    const evaluations = evaluateSegmentProviderOffers( context );

    if ( pricingPreferences.swissReductionCard === "ga" && isSwissCffPricingSegment ) {
        const swissEvaluation = evaluations.find( ( evaluation ) => evaluation.providerKey === "swiss_cff" );
        const linkUrls = buildSegmentOfferLinkUrls( itinerary, segmentId, swissEvaluation?.bookingUrl ?? null );

        return {
            "providerKey":  "swiss_cff",
            "providerName": providerLabelFor( "swiss_cff" ),
            "price":        null,
            ...linkUrls,
            "isFinal":      itinerarySegment?.isFinal ?? null,
        };
    }

    const selectedEvaluation = selectPreferredEvaluation( evaluations );

    if ( selectedEvaluation === null ) {
        return null;
    }

    const linkUrls = buildSegmentOfferLinkUrls( itinerary, segmentId, selectedEvaluation.bookingUrl );

    return {
        "providerKey":  selectedEvaluation.providerKey,
        "providerName": selectedEvaluation.providerName,
        "price":        normalizeEvaluatedPriceForDisplay( selectedEvaluation.price ),
        ...linkUrls,
        "isFinal":      itinerarySegment?.isFinal ?? null,
    };
};

/**
 * Computes the itinerary offer total as the sum of selected segment display prices.
 */
export const computeItineraryOfferTotalPrice = (
    itinerary: ItineraryDTO,
    pricingPreferences: ItineraryPricingPreferencesDTO,
): number | null => {
    const pricingSegments = itinerary.pricingSegments;
    if ( pricingSegments === undefined || pricingSegments.length === 0 ) {
        return null;
    }

    const prices = pricingSegments.map( ( segment ) => {
        return normalizeEvaluatedPriceForDisplay( computeSegmentOfferDisplayData( itinerary, segment.id, pricingPreferences )?.price ?? null );
    } ).filter( ( price ): price is number => price !== null );

    if ( prices.length === 0 ) {
        return null;
    }

    const total = prices.reduce( ( sum, price ) => sum + price, 0 );

    return normalizeEvaluatedPriceForDisplay( total );
};

/**
 * Resolves a itinerary header link for rental-only offer fallbacks (no numeric total).
 *
 * @remarks Prefers the first direct offer URL across rental segments, then the first provider-site URL.
 *
 * @param itinerary - Loaded itinerary with pricing segments and offer payload.
 * @param pricingPreferences - Active pricing preferences.
 * @returns Link target and label, or `null` when no rental segment exposes a URL.
 */
export const computeRentalItineraryOfferLinkDisplay = (
    itinerary: ItineraryDTO,
    pricingPreferences: ItineraryPricingPreferencesDTO,
): ItineraryDetailsOfferLinkDisplay | null => {
    const pricingSegments = itinerary.pricingSegments;
    if ( pricingSegments === undefined || pricingSegments.length === 0 ) {
        return null;
    }

    let firstOfferBookingUrl: string | null = null;
    let firstProviderSiteUrl: string | null = null;

    for ( const segment of pricingSegments ) {
        if ( segment.provider?.id !== SegmentationProviderId.rental_motis ) {
            continue;
        }

        const segmentDisplay = computeSegmentOfferDisplayData( itinerary, segment.id, pricingPreferences );
        if ( segmentDisplay === null ) {
            continue;
        }

        if ( firstOfferBookingUrl === null && segmentDisplay.offerBookingUrl !== null ) {
            firstOfferBookingUrl = segmentDisplay.offerBookingUrl;
        }

        if ( firstProviderSiteUrl === null && segmentDisplay.providerSiteUrl !== null ) {
            firstProviderSiteUrl = segmentDisplay.providerSiteUrl;
        }
    }

    if ( firstOfferBookingUrl !== null ) {
        return {
            "bookingUrl": firstOfferBookingUrl,
            "label":      SEGMENT_OFFER_DIRECT_LINK_LABEL,
        };
    }

    if ( firstProviderSiteUrl !== null ) {
        return {
            "bookingUrl": firstProviderSiteUrl,
            "label":      SEGMENT_OFFER_PROVIDER_SITE_LINK_LABEL,
        };
    }

    return null;
};

export type { SegmentOfferDisplayData } from "./OfferPricing.types";

export type { ItineraryDetailsOfferLinkDisplay } from "../itineraryDetailsOfferLink.types";
